/**
 * Shared CURB/ROI fee calculation logic used by RAP ROI Fee milestone and Add Fees form.
 */

const CURB_HIGH_PERCENT = 49.8;
const CURB_MEDIUM_PERCENT = 33.2;
const CURB_LOW_PERCENT = 24.9;
const CURB_FACTOR_COUNT = 4;

export interface CurbFeeTypeRow {
  type: string;
  priority?: string;
  high?: boolean;
  medium?: boolean;
  low?: boolean;
}

export interface ProposedFeesInput {
  year1Savings: number | null | undefined;
  year2Savings: number | null | undefined;
  implementationFeeTypes: CurbFeeTypeRow[];
  thirdPartyFee?: number | null | undefined;
}

export interface ProposedFeesResult {
  y1Proposed: number;
  y2Proposed: number;
  avgPercent: number;
}

/**
 * Computes the average CURB percent from implementation fee type priorities.
 * Only rows with type !== 'Select All' are included; percentSum is divided by 4.
 */
export function getCurbAvgPercent(implementationFeeTypes: CurbFeeTypeRow[]): number {
  let percentSum = 0;
  for (const line of implementationFeeTypes) {
    if (line.type === 'Select All') continue;
    if (line.priority === 'high') percentSum += CURB_HIGH_PERCENT;
    else if (line.priority === 'medium') percentSum += CURB_MEDIUM_PERCENT;
    else if (line.priority === 'low') percentSum += CURB_LOW_PERCENT;
  }
  return percentSum / CURB_FACTOR_COUNT;
}

/**
 * Calculates proposed Y1 and Y2 fees from CURB factors and optional third-party fee.
 * - Y1: base CURB fee (year1Savings * avgPercent / 100), then subtract thirdPartyFee
 *       only if thirdPartyFee is a positive number and the result would be non-negative.
 * - Y2: year2Savings * avgPercent / 100 (no third-party subtraction).
 */
export function calculateProposedFees(input: ProposedFeesInput): ProposedFeesResult {
  const avgPercent = getCurbAvgPercent(input.implementationFeeTypes);
  const year1 = input.year1Savings != null ? Number(input.year1Savings) : 0;
  const year2 = input.year2Savings != null ? Number(input.year2Savings) : 0;

  let y1Proposed = 0;
  if (year1) {
    const baseCurbFee = Math.round((year1 * avgPercent) / 100);
    const thirdPartyAmount = input.thirdPartyFee != null ? Number(input.thirdPartyFee) : NaN;
    const hasThirdPartyFee =
      !Number.isNaN(thirdPartyAmount) && thirdPartyAmount > 0 && baseCurbFee - thirdPartyAmount >= 0;
    y1Proposed = hasThirdPartyFee ? Math.round(baseCurbFee - thirdPartyAmount) : baseCurbFee;
  }

  const y2Proposed = year2 ? (year2 * avgPercent) / 100 : 0;

  return { y1Proposed, y2Proposed, avgPercent };
}
